import './RelatedDeviceList.css';
import type { NodeId, NodeType } from '@ipfabric/utils';
import classNames from 'classnames';
import { memo, useMemo } from 'react';
import type { FC } from 'react';

export type RelatedDeviceItem = {
  hostname: string;
  id: NodeId;
  parentId: NodeId;
  type: NodeType;
};

type Props = {
  className?: string;
  maxChildrenToRender?: number;
  relatedDevices: RelatedDeviceItem[];
};

type RelatedDeviceParent = RelatedDeviceItem & {
  children: RelatedDeviceItem[];
};

const RelatedDevice = (props: {
  device: RelatedDeviceItem;
  className?: string;
}) => (
  <div className={props.className}>
    {props.device.hostname}{' '}
    <span className='text-gray-800'>{props.device.type}</span>
  </div>
);

const buildRelatedDevicesTree = (
  relatedDevices: RelatedDeviceItem[],
): RelatedDeviceParent[] => {
  const children = new Map<NodeId, RelatedDeviceItem[]>();
  const parents: RelatedDeviceItem[] = [];
  for (const device of relatedDevices) {
    if (device.id === device.parentId) {
      parents.push(device);
    } else {
      if (!children.has(device.parentId)) {
        children.set(device.parentId, []);
      }
      children.get(device.parentId)!.push(device);
    }
  }
  return parents
    .filter((parent) => children.has(parent.id))
    .map((parent) => ({ ...parent, children: children.get(parent.id)! }));
};

const BareRelatedDeviceList: FC<Props> = (props) => {
  const relatedDevicesTree = useMemo(
    () => buildRelatedDevicesTree(props.relatedDevices),
    [props.relatedDevices],
  );
  const maxChildrenToRender = props.maxChildrenToRender ?? 5;
  const listMoreMsg = '+ {number} more';
  return (
    <div className={classNames('flex flex-col gap-4', props.className)}>
      {relatedDevicesTree.map((parent) => (
        <div key={parent.id}>
          <RelatedDevice device={parent} />
          {parent.children.slice(0, maxChildrenToRender).map((child) => (
            <RelatedDevice
              key={child.id}
              className='related-device-list__child'
              device={child}
            />
          ))}
          {parent.children.length > maxChildrenToRender && (
            <div className='related-device-list__child text-gray-800'>
              {listMoreMsg.replace(
                '{number}',
                (parent.children.length - maxChildrenToRender).toString(),
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export const RelatedDeviceList = memo(BareRelatedDeviceList);
